import _get from 'lodash/get';
import { GetUrlParams } from '@lib/enums/urls';
import { fetchDataFromDB } from 'hooks/useTasksIndexedDB';
import { APP_URLS } from 'constants/urls';
import QrCodePrefix from '@lib/enums/qrCodePrefix';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import { logger } from '@lib/services/logger';
import { ScannerActionType } from '../enums';
import { CodeType } from './getCodeType';
import { ParsedCodes } from './getParsedCodes';
import { HandleQrCodeSuccessProps } from './types';

export default async function handleValidCodeForTasksActionFromIndexedDB(
  data: string,
  {
    setCode,
    navigate,
    onSuccessScanAction,
    setLoading,
    setErrorStatus,
  }: HandleQrCodeSuccessProps,
  parsedCodes: ParsedCodes,
  codeType: CodeType,
) {
  try {
    const filterParams: Record<string, any> = {};
    const { customPrefix, entityCode, sideCode } = parsedCodes;

    if (sideCode) {
      filterParams.siteCode = [{ value: sideCode }];
    }
    if (entityCode && codeType === CodeType.SiteArea) {
      filterParams.siteAreaCode = [{ value: entityCode }];
    }
    if (entityCode && codeType === CodeType.Asset) {
      filterParams.assetCode = [{ value: entityCode }];
    }
    if (
      customPrefix &&
      codeType === CodeType.Asset &&
      customPrefix !== QrCodePrefix.SiteArea &&
      customPrefix !== QrCodePrefix.Asset
    ) {
      filterParams.assetQrPrefix = [{ value: customPrefix }];
    }

    logger.debug('Scanner: IndexedDB - querying with filters', {
      filterParams,
      codeType,
    });

    setLoading(true);
    const { paginatedData: allMatchingTasks } = await fetchDataFromDB(
      filterParams,
      {
        [GetUrlParams.PerPage]: 2,
        [GetUrlParams.Page]: 1,
      },
    );

    // For SiteArea codes, exclude tasks that have an asset attached
    // (matches the online API filter: assetId: { null: true })
    const tasks =
      codeType === CodeType.SiteArea
        ? allMatchingTasks.filter((task) => !task.asset)
        : allMatchingTasks;

    // Use pre-filtered results for site/siteArea entity lookups (similar to
    // online path's separate siteFindBy/siteAreaFindBy queries)
    const tasksForEntityLookup =
      allMatchingTasks.length > 0 ? allMatchingTasks : tasks;

    logger.debug('Scanner: IndexedDB - query results', {
      allMatchingTasksCount: allMatchingTasks.length,
      filteredTasksCount: tasks.length,
      entityLookupCount: tasksForEntityLookup.length,
      codeType,
    });

    if (tasks.length === 1) {
      logger.info('Scanner: IndexedDB - single task found, navigating', {
        taskId: tasks[0].id,
      });
      setCode(data);
      navigate(
        APP_URLS.app.tasks.completionCreate.getDynamicPath({
          pathParts: { id: tasks[0].id },
        }),
      );
    } else if ((sideCode || entityCode) && tasksForEntityLookup.length) {
      const site = _get(tasksForEntityLookup, [0, 'site']);
      const siteArea = _get(tasksForEntityLookup, [0, 'siteArea']);
      const asset = _get(tasksForEntityLookup, [0, 'asset']);
      const values = {
        [TasksFilterFieldNames.Site]:
          sideCode && site && sideCode === site.code
            ? [
                {
                  value: site.id,
                  label: site.name,
                },
              ]
            : undefined,
        [TasksFilterFieldNames.SiteArea]:
          entityCode &&
          siteArea &&
          codeType === CodeType.SiteArea &&
          entityCode === siteArea.code
            ? [
                {
                  value: siteArea.id,
                  label: siteArea.name,
                },
              ]
            : undefined,
        [TasksFilterFieldNames.Asset]:
          entityCode &&
          asset &&
          codeType === CodeType.Asset &&
          entityCode === asset.code
            ? [
                {
                  value: asset.id,
                  label: asset.name,
                },
              ]
            : undefined,
      };
      if (
        values[TasksFilterFieldNames.Site] === undefined ||
        (values[TasksFilterFieldNames.SiteArea] === undefined &&
          values[TasksFilterFieldNames.Asset] === undefined)
      ) {
        logger.warn(
          'Scanner: IndexedDB - entity lookup failed (site/siteArea/asset not resolved)',
          {
            siteId: site?.id,
            siteName: site?.name,
            siteAreaId: siteArea?.id,
            siteAreaName: siteArea?.name,
            assetId: asset?.id,
            assetName: asset?.name,
          },
        );
        setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
      } else {
        logger.info(
          'Scanner: IndexedDB - navigating to task list with filters',
          {
            siteId: site?.id,
            siteName: site?.name,
            siteAreaId: siteArea?.id,
            siteAreaName: siteArea?.name,
            assetId: asset?.id,
            assetName: asset?.name,
          },
        );
        setCode(data);
        onSuccessScanAction(values, ScannerActionType.FindTasks);
      }
    } else {
      logger.warn('Scanner: IndexedDB - no matching tasks found');
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
    }
  } catch (error) {
    logger.error('Scanner: IndexedDB - unexpected error', {
      error: error instanceof Error ? error.message : String(error),
    });
  } finally {
    setLoading(false);
  }
}
